/**
 * Text element renderer
 * Extracted from Canvas.jsx to reduce file size
 */
import React from "react";
import TextElement from "../../text/TextElement";
import { isShapeType } from "../../../../../util/shapeTypeMapper";
import { isElementInViewport } from "../../../utils/viewportCullingUtils";
import { isPrimaryMouseButton } from "../../../utils/pointerButtonUtils";
import { getStageHeightPxFromCanvasHeight } from "../../../utils/mainCanvasLayout";
import {
  hasUserContent,
  hasTaskContent,
  hasNoteContent,
  formatListContent,
  stripHtmlPreserveFormatting,
} from "../../../utils/textCanvasUtils";

// Throttle React state updates during drag to cut parent re-renders (Konva position updates every frame).
// Reduced from 80ms to 16ms for better cross-canvas drag performance
const DRAG_STATE_THROTTLE_MS = 16;
let _lastTextDragStateTime = 0;

// Cache text dimensions (width/height) so we don't run calculateGroupHeight/calculateTextBlockWidth on every render.
// Both call measureHtmlHeight repeatedly; during drag the parent re-renders often and we were recalculating for every text.
const DIM_CACHE_MAX = 100;
const _textDimCache = new Map();

function getTextDimKey(t) {
  if (!t) return "";
  // Exclude x,y so drag (position change) does not invalidate cache
  return `${t.id}|${(t.content || "").length}|${(t.task || "").length}|${(t.note || "").length}|${t.width ?? ""}|${t.fontSize ?? ""}`;
}

function getCachedDimensions(latestText, calcWidth, calcHeight) {
  const key = getTextDimKey(latestText);
  let cached = _textDimCache.get(key);
  if (cached) return cached;
  const width = calcWidth(latestText);
  const height = calcHeight(latestText);
  cached = { width, height };
  if (_textDimCache.size >= DIM_CACHE_MAX) {
    const firstKey = _textDimCache.keys().next().value;
    if (firstKey !== undefined) _textDimCache.delete(firstKey);
  }
  _textDimCache.set(key, cached);
  return cached;
}

/**
 * Creates a text element renderer factory function
 */
export const createTextRenderer = ({
  texts,
  offCanvasTexts,
  setTexts,
  setOffCanvasTexts,
  selectedObject,
  selectedObjects = [], // Add selectedObjects parameter
  canvasHeight,
  canvasResizeTrigger,
  handleElementDragEnd,
  handleTransformUpdate,
  effectiveZoomLevel,
  disableInteraction,
  offCanvasTransformerRef,
  transformerRef,
  setSelectedText,
  setSelectedObject,
  setSelectedObjects,
  calculateTextBlockWidth: calculateTextBlockWidthUtil,
  getTextStyle,
  calculateGroupHeight,
  setSelectedImage,
  setSelectedTextId,
  setShowTextBox,
  selectedText,
  setEditTextBoxContent,
  editTextBoxContent,
  setShowEditTask,
  setShowEditNote,
  showEditTask,
  showEditNote,
  handlePartClick,
  estimateKonvaTextHeight,
  onClick,
  setAction,
  ACTIONS,
  action,
  setSelectedObjects: setSelectedObjectsFn,
  setEditing,
  isResizing,
  handleDragPreview,
  clearDragPreview,
  handleElementCopy,
  lockedAction,
  isCloneMode = false,
  isMultiSelectMode = false, // Multi-select mode flag
  isMultiSelectModeRef = null, // Ref to avoid stale closures
  handleGroupDragStart = null, // Handler for group drag start
  handleGroupDragMove = null, // Handler for group drag move
  isSelecting = false, // Selection box is being drawn
  stageRef = null, // Main canvas stage ref
  offCanvasStageRef = null, // Off-canvas stage ref
  blockClicksOnOffCanvasElements = false,
  blockClicksOnMainStageElements = false,
  dragPreviewStateRef = null, // Last drag preview state; use for drop position when main→off→main
  crossStageGestureBegin = null,
  crossStageGestureEnd = null,
}) => {
  return ({
    text,
    isOffCanvas = false,
    sourceStageRef,
    isPreview = false,
    onTransformEnd: onTransformEndOverride = null,
    opacity = undefined,
    hideBecauseGhostOnMain = false,
  }) => {
    // Use provided opacity or element's own opacity, defaulting to 1
    // When hideBecauseGhostOnMain (main→off→main ghost on main), hide source text at boundary so only ghost is visible
    const elementOpacity = hideBecauseGhostOnMain
      ? 0
      : opacity !== undefined
        ? opacity
        : text.opacity !== undefined
          ? text.opacity
          : 1;
    const setTextState = isOffCanvas ? setOffCanvasTexts : setTexts;
    const textsArray = isOffCanvas ? offCanvasTexts : texts;

    // Use selectedText for live editor content updates, but always prefer the
    // latest geometry from canvas state arrays so draw-end clamp reflects
    // immediately without requiring deselect/reselect.
    const isThisTextSelected = selectedText && selectedText.id === text.id;
    const textFromArray = textsArray?.find((t) => t.id === text.id) || null;
    const latestText = isThisTextSelected
      ? {
          ...selectedText,
          // Geometry from state array wins to avoid stale selectedText coords.
          ...(textFromArray
            ? {
                x: textFromArray.x,
                y: textFromArray.y,
                width: textFromArray.width,
                height: textFromArray.height,
                rotation: textFromArray.rotation,
              }
            : {}),
        }
      : textFromArray || text;

    const isInteractive = !isPreview;
    const isSelected =
      isInteractive &&
      latestText.id === selectedObject?.id &&
      isShapeType(selectedObject?.type, "Text");

    // Check if element is in selectedObjects array (for multi-selection)
    const isInSelectedObjects =
      selectedObjects &&
      selectedObjects.some(
        (obj) => obj.id === latestText.id && obj.type === "Text",
      );

    // When duplicate-to-selected-location is on, no drag. Otherwise draggable if selected or in multi-selection.
    const isDraggableElement =
      isInteractive &&
      !disableInteraction &&
      !isCloneMode &&
      !lockedAction &&
      (isSelected || isInSelectedObjects);

    // Get current canvas dimensions
    const stageWidth = sourceStageRef?.width() || window.innerWidth * 0.45;
    const MAX_WIDTH = Math.max(10, stageWidth);
    // Use real stage height when available; fallback to fixed stage-height mapping.
    const stageHeightFromRef = sourceStageRef?.height?.();
    const stageHeight =
      Number.isFinite(stageHeightFromRef) && stageHeightFromRef > 0
        ? stageHeightFromRef
        : Number(canvasHeight) > 100
          ? Number(canvasHeight)
          : getStageHeightPxFromCanvasHeight(canvasHeight);

    // Pass all necessary props from Canvas to TextElement
    // CRITICAL: Include selectedContent.id in key to force re-render when selection changes
    // This ensures TextElement remounts when selectedContent changes, picking up the latest state
    const selectedContentId = latestText.selectedContent?.id || "";

    // Debug: Log when we detect a mismatch - this helps track if the sync is working
    if (latestText.selectedContent?.id !== text.selectedContent?.id) {
    }

    // CRITICAL: Use onTransformEndOverride if provided (from Canvas.jsx), otherwise use handleTransformUpdate
    // This allows Canvas.jsx to pass a custom handler that stores in the ref for history saving
    const transformEndHandler = onTransformEndOverride || handleTransformUpdate;

    // Viewport culling: skip rendering text far outside visible area (TextElement with HTML is expensive)
    const shouldAlwaysRender = isPreview || isSelected || isInSelectedObjects;
    if (!shouldAlwaysRender) {
      const stage = isOffCanvas
        ? offCanvasStageRef?.current
        : stageRef?.current;
      const textWithBounds = {
        ...latestText,
        width: latestText.width ?? 100,
        height: latestText.height ?? 50,
      };
      if (stage && !isElementInViewport(textWithBounds, "Text", stage)) {
        return null;
      }
    }

    // NOTE: Do NOT include _lastUpdated in the key here.
    // Including it causes TextElement to unmount/remount on every debounced content update,
    // which destroys the Konva Group node and detaches the transformer — making the blue
    // transformer rect disappear until the next Canvas re-render.
    // Content updates flow through the `text` prop (via latestText = selectedText) so no remount is needed.
    const textDims = getCachedDimensions(
      latestText,
      (t) => calculateTextBlockWidthUtil(t, getTextStyle),
      (t) => calculateGroupHeight(t),
    );
    return (
      <TextElement
        key={`${latestText.id}-${selectedContentId}`}
        onTransformEnd={transformEndHandler}
        text={{ ...latestText, opacity: elementOpacity }}
        MAX_WIDTH={MAX_WIDTH}
        offCanvasTexts={offCanvasTexts}
        effectiveZoomLevel={effectiveZoomLevel}
        setOffCanvasTexts={setOffCanvasTexts}
        setTexts={isOffCanvas ? setOffCanvasTexts : setTexts}
        texts={textsArray}
        handleElementDragEnd={isInteractive ? handleElementDragEnd : () => {}}
        isDraggable={isDraggableElement}
        disableInteraction={disableInteraction || !isInteractive}
        onDragMove={
          isInteractive
            ? (newAttrs, event) => {
                // If multiple elements are selected AND this element is part of the selection,
                // use group drag handler instead of individual drag
                const isInSelectedObjects = selectedObjects?.some(
                  (obj) => obj.id === newAttrs.id && obj.type === "Text",
                );
                if (
                  selectedObjects &&
                  selectedObjects.length > 1 &&
                  isInSelectedObjects
                ) {
                  // Use group drag handler - pass event for accurate boundary detection
                  // NOTE: handleGroupDragStart is called in onDragStart (TextElement.jsx) so
                  // initial positions are captured before any movement occurs.
                  handleGroupDragMove?.(
                    newAttrs.id,
                    "Text",
                    newAttrs.x,
                    newAttrs.y,
                    event,
                  );
                  return;
                }

                // Normal individual drag behavior
                let displayAttrs = { ...newAttrs };

                const now = Date.now();
                const shouldSync =
                  now - _lastTextDragStateTime >= DRAG_STATE_THROTTLE_MS;

                // Cross-canvas transfer is disabled; keep clamped source-stage coordinates.

                // CRITICAL FIX: Pass SOURCE-STAGE coords to handleDragPreview
                // handleDragPreview expects coordinates in the source stage coordinate system
                // and will do its own conversion based on mouse position
                const previewAttrs = {
                  ...newAttrs,
                  x:
                    newAttrs._stageX !== undefined
                      ? newAttrs._stageX
                      : newAttrs.x,
                  y:
                    newAttrs._stageY !== undefined
                      ? newAttrs._stageY
                      : newAttrs.y,
                };

                if (shouldSync) {
                  handleDragPreview?.({
                    event,
                    newAttrs: previewAttrs,
                    sourceStageRef,
                    element: latestText,
                    type: "Text",
                  });
                }

                // TextElement syncs HTML in onDragMove via syncHtmlPositionToNode. Keep transformer in sync.
                // Update selectedText/selectedObject during drag so TextBoxWithButtons shows live x/y.
                // IMPORTANT: Do NOT propagate _isInOffCanvasDuringDrag into selection state during drag.
                // Moving the transformer/editor to the other stage mid‑drag can interrupt Konva's drag
                // and fire dragEnd prematurely (same issue we fixed for rectangles).
                if (shouldSync) {
                  const {
                    _isInOffCanvasDuringDrag: _unusedDragFlag,
                    ...selectionDisplayAttrs
                  } = displayAttrs;

                  const stateUpdate = {
                    ...latestText,
                    ...selectionDisplayAttrs,
                    type: "Text",
                  };

                  _lastTextDragStateTime = now;
                  setSelectedText(stateUpdate);
                  setSelectedObject(stateUpdate);
                  setSelectedObjects((prev) => {
                    const existingIndex = prev.findIndex(
                      (obj) => obj.id === newAttrs.id && obj.type === "Text",
                    );
                    if (existingIndex >= 0) {
                      const updated = [...prev];
                      updated[existingIndex] = stateUpdate;
                      return updated;
                    }
                    // IMPORTANT: For single-text drag, don't inject this element into selectedObjects.
                    // Canvas selected overlay rendering uses selectedObjects and can double-render
                    // the same Text (base layer + selected layer), causing jitter/ghost copies.
                    // Keep selectedObjects untouched unless this text is already part of a multi-selection.
                    return prev;
                  });
                }

                // Ensure the correct transformer updates on every move
                const tr = isOffCanvas
                  ? offCanvasTransformerRef?.current
                  : transformerRef?.current;
                if (tr) {
                  if (shouldSync && typeof tr.forceUpdate === "function") {
                    tr.forceUpdate();
                  }
                  tr.getLayer()?.batchDraw();
                }
              }
            : undefined
        }
        handleDragPreview={handleDragPreview}
        sourceStageRef={sourceStageRef}
        crossStageGestureBegin={crossStageGestureBegin}
        crossStageGestureEnd={crossStageGestureEnd}
        onDragEnd={
          isInteractive
            ? (
                id,
                newX,
                newY,
                mouseClientX,
                mouseClientY,
                currentText = null,
              ) => {
                // Use preview position when available so drop lands at cursor (match rectangle behavior).
                // main→off: preview.x/y in off-canvas coords. off→main: preview.x/y in main coords. main→off→main: main coords.
                let dropX = newX;
                let dropY = newY;
                let dropPositionInOffCanvasCoords = false;
                let dropPositionInMainCoords = false;
                const preview = dragPreviewStateRef?.current;
                if (
                  preview &&
                  preview.type === "Text" &&
                  preview.id === id &&
                  typeof preview.x === "number" &&
                  typeof preview.y === "number"
                ) {
                  dropX = preview.x;
                  dropY = preview.y;
                  if (preview.target === "offCanvas") {
                    dropPositionInOffCanvasCoords = true; // main→off: dropX/dropY already off-canvas stage coords
                  } else if (preview.target === "main" && isOffCanvas) {
                    dropPositionInMainCoords = true; // off→main: dropX/dropY already main stage coords
                  }
                }

                clearDragPreview?.();

                // CRITICAL: Get the current text properties from the Konva node if not provided
                // The node has the actual current rotation, scaleX, scaleY, etc. that the user sees
                let textWithRotation = currentText;
                if (!textWithRotation) {
                  const stage = sourceStageRef?.current || sourceStageRef;
                  if (stage) {
                    const node = stage.findOne(`#${id}`);
                    if (node) {
                      const baseText = (
                        isOffCanvas ? offCanvasTexts : texts
                      ).find((text) => text.id === id);

                      if (baseText) {
                        let nodeRotation = node.rotation();
                        nodeRotation = nodeRotation % 360;
                        if (nodeRotation < 0) nodeRotation += 360;

                        textWithRotation = {
                          ...baseText,
                          x: dropX,
                          y: dropY,
                          rotation: nodeRotation,
                          scaleX: node.scaleX(),
                          scaleY: node.scaleY(),
                        };
                      }
                    }
                  }
                }

                // Fallback: if we couldn't get the node, use the text from state
                if (!textWithRotation) {
                  textWithRotation = (
                    isOffCanvas ? offCanvasTexts : texts
                  ).find((text) => text.id === id);
                  if (textWithRotation) {
                    textWithRotation = {
                      ...textWithRotation,
                      x: dropX,
                      y: dropY,
                    };
                  }
                }

                // Ensure drag-end clamping uses the latest rendered text bounds,
                // not stale/default height (e.g. 35), so bottom-boundary clamp
                // works immediately even before unselect.
                if (textWithRotation) {
                  textWithRotation = {
                    ...textWithRotation,
                    height: Math.max(
                      Number(textWithRotation.height) || 0,
                      Number(textDims.height) || 0,
                    ),
                    width: Math.max(
                      Number(textWithRotation.width) || 0,
                      Number(textDims.width) || 0,
                    ),
                  };
                }

                // Don't update state here - let handleElementDragEnd handle all updates
                // This ensures coordinates are properly converted when element is dragged below main canvas
                // Match rectangle's approach exactly - handleElementDragEnd is a wrapper that adds stageRef, offCanvasStageRef, etc.
                if (handleElementDragEnd) {
                  handleElementDragEnd({
                    id,
                    newX: dropX,
                    newY: dropY,
                    type: "Text",
                    sourceStageRef,
                    mainState: texts,
                    offCanvasState: offCanvasTexts,
                    setMainState: setTexts,
                    setOffCanvasState: setOffCanvasTexts,
                    setSelectedObjects: setSelectedObjectsFn,
                    mouseClientX,
                    mouseClientY,
                    currentText: textWithRotation,
                    dropPositionInOffCanvasCoords,
                    dropPositionInMainCoords, // true when off→main: dropX/dropY are already main stage coords
                  });
                }
              }
            : undefined
        }
        stageRef={sourceStageRef}
        textBlockWidth={textDims.width}
        textGroupHeight={textDims.height}
        calculateGroupHeight={calculateGroupHeight}
        formatListContent={formatListContent}
        getTextStyle={getTextStyle}
        setSelectedImage={setSelectedImage}
        setSelectedTextId={setSelectedTextId}
        setShowTextBox={setShowTextBox}
        transformerRef={isOffCanvas ? offCanvasTransformerRef : transformerRef}
        selectedObject={selectedObject}
        setSelectedObject={setSelectedObject}
        selectedText={selectedText}
        setEditTextBoxContent={setEditTextBoxContent}
        editTextBoxContent={editTextBoxContent}
        setShowEditTask={setShowEditTask}
        setShowEditNote={setShowEditNote}
        showEditTask={showEditTask}
        showEditNote={showEditNote}
        setSelectedText={setSelectedText}
        handlePartClick={handlePartClick}
        hasUserContent={hasUserContent}
        estimateKonvaTextHeight={estimateKonvaTextHeight}
        stripHtmlPreserveFormatting={stripHtmlPreserveFormatting}
        hasTaskContent={hasTaskContent}
        hasNoteContent={hasNoteContent}
        blockClicksOnOffCanvasElements={blockClicksOnOffCanvasElements}
        blockClicksOnMainStageElements={blockClicksOnMainStageElements}
        onClick={
          onClick
            ? (e) => {
                if (!isPrimaryMouseButton(e.evt)) {
                  return;
                }
                if (isOffCanvas && blockClicksOnOffCanvasElements) {
                  e?.evt?.preventDefault?.();
                  e?.evt?.stopPropagation?.();
                  return;
                }
                if (!isOffCanvas && blockClicksOnMainStageElements) {
                  e?.evt?.preventDefault?.();
                  e?.evt?.stopPropagation?.();
                  return;
                }
                // Keep text selection one-click consistent with other renderers.
                // Set primary selection immediately, then let shared click handler
                // run additional logic (multi-select/cross-canvas/text-part behavior).
                if (!isMultiSelectMode && !lockedAction && !isCloneMode) {
                  const textAsObj = { ...latestText, type: "Text" };
                  setSelectedImage(null);
                  setSelectedText(textAsObj);
                  setSelectedTextId(latestText.id);
                  setShowTextBox(true);
                  setSelectedObject(textAsObj);
                  setSelectedObjectsFn([textAsObj]);
                }
                onClick(e);
              }
            : undefined
        }
        setAction={setAction}
        ACTIONS={ACTIONS}
        action={action}
        setSelectedObjects={setSelectedObjectsFn}
        selectedObjects={selectedObjects}
        setEditing={setEditing}
        canvasWidth={stageWidth}
        canvasHeight={stageHeight}
        canvasResizeTrigger={canvasResizeTrigger}
        isResizing={isResizing}
        isOffCanvas={isOffCanvas}
        isPreview={isPreview}
        isSelected={isSelected}
        lockedAction={lockedAction}
        isMultiSelectMode={isMultiSelectMode}
        isMultiSelectModeRef={isMultiSelectModeRef} // Pass ref to avoid stale closures
        handleGroupDragStart={handleGroupDragStart} // Called in onDragStart to capture positions before movement
        onCopyToOppositeCanvas={
          isInteractive && handleElementCopy && isOffCanvas
            ? () => handleElementCopy(latestText.id, "Text", "offCanvas")
            : undefined
        }
        isSelecting={isSelecting} // Pass isSelecting to disable listening on Rect during selection
      />
    );
  };
};
